import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    createBusinessAccount,
    createBusinessOrganization,
    createCheckboxExclusion,
    createCheckboxRegister,
    createInternalTransferRule,
    getBusinessOrganizations,
    getInternalTransferRules,
    testCheckboxRegister,
    updateBusinessAccount,
    updateBusinessOrganization,
    updateCheckboxExclusion,
    updateCheckboxRegister,
    updateInternalTransferRule,
} from "../api/businessOrganizationsApi";
import { getBusinessStores } from "../api/suppliersApi";
import { handleAuthError } from "../api/developerApi";
import { recalculatePaymentReport } from "../api/paymentReportsApi";

const pageStyle = { padding: "24px", maxWidth: "1440px", margin: "0 auto", display: "grid", gap: "20px" };
const embeddedPageStyle = { ...pageStyle, padding: 0 };
const hiddenCheckboxStyle = { position: "absolute", opacity: 0, width: 0, height: 0, margin: 0 };

const emptyOrganization = {
    salesdrive_organization_id: "",
    short_name: "",
    full_name: "",
    tax_id: "",
    entity_type: "other",
    verification_status: "needs_review",
    vat_enabled: false,
    vat_payer: false,
    without_stamp: false,
    signer_name: "",
    signer_position: "",
    chief_accountant_name: "",
    cashier_name: "",
    address: "",
    postal_code: "",
    city: "",
    region: "",
    country: "Україна",
    phone: "",
    order_supplier_selection_policy: "lowest_cost",
    is_active: true,
    notes: "",
};

const emptyAccount = {
    salesdrive_account_id: "",
    account_number: "",
    account_title: "",
    label: "",
    card_mask: "",
    currency: "UAH",
    bank_name: "",
    mfo: "",
    is_active: true,
};

const emptyRegister = {
    business_store_id: "",
    enterprise_code: "",
    register_name: "",
    cash_register_code: "",
    checkbox_license_key: "",
    cashier_login: "",
    cashier_password: "",
    cashier_pin: "",
    api_base_url: "https://api.checkbox.ua",
    is_test_mode: true,
    is_active: true,
    is_default: false,
    shift_open_mode: "on_fiscalization",
    shift_open_time: "",
    shift_close_time: "23:50",
    timezone: "Europe/Kiev",
    receipt_notifications_enabled: false,
    shift_notifications_enabled: true,
    notes: "",
};

const emptyExclusion = {
    cash_register_id: "",
    supplier_code: "",
    supplier_name: "",
    is_active: true,
    comment: "",
};

const emptyInternalTransferRule = {
    from_account_id: "",
    to_account_id: "",
    is_active: true,
    pairing_window_minutes: "5",
    require_exact_amount: true,
    create_reverse: true,
    notes: "",
};

const currentMonthPeriod = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return { periodFrom: `${year}-${month}-01`, periodTo: `${year}-${month}-${day}` };
};

const normalizeText = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized || null;
};

const normalizeId = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized ? Number(normalized) : null;
};

const formatError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
        return detail.map((item) => item.msg || JSON.stringify(item)).join("; ");
    }
    return detail || error?.message || fallback;
};

const organizationToDraft = (item) => ({
    ...emptyOrganization,
    ...item,
    salesdrive_organization_id: item?.salesdrive_organization_id || "",
    short_name: item?.short_name || "",
    full_name: item?.full_name || "",
    tax_id: item?.tax_id || "",
    signer_name: item?.signer_name || "",
    signer_position: item?.signer_position || "",
    chief_accountant_name: item?.chief_accountant_name || "",
    cashier_name: item?.cashier_name || "",
    address: item?.address || "",
    postal_code: item?.postal_code || "",
    city: item?.city || "",
    region: item?.region || "",
    country: item?.country || "",
    phone: item?.phone || "",
    order_supplier_selection_policy: item?.order_supplier_selection_policy || "lowest_cost",
    notes: item?.notes || "",
});

const accountToDraft = (item) => ({
    ...emptyAccount,
    ...item,
    salesdrive_account_id: item?.salesdrive_account_id || "",
    account_number: item?.account_number || "",
    account_title: item?.account_title || "",
    label: item?.label || "",
    card_mask: item?.card_mask || "",
    currency: item?.currency || "UAH",
    bank_name: item?.bank_name || "",
    mfo: item?.mfo || "",
});

const registerToDraft = (item) => ({
    ...emptyRegister,
    ...item,
    business_store_id: item?.business_store_id ?? "",
    enterprise_code: item?.enterprise_code || "",
    checkbox_license_key: "",
    cashier_password: "",
    cashier_pin: "",
    cashier_login: item?.cashier_login || "",
    api_base_url: item?.api_base_url || "https://api.checkbox.ua",
    shift_open_time: item?.shift_open_time || "",
    shift_close_time: item?.shift_close_time || "",
    notes: item?.notes || "",
});

const exclusionToDraft = (item) => ({
    ...emptyExclusion,
    ...item,
    cash_register_id: item?.cash_register_id ?? "",
    supplier_code: item?.supplier_code || "",
    supplier_name: item?.supplier_name || "",
    comment: item?.comment || "",
});

const internalTransferRuleToDraft = (item) => ({
    ...emptyInternalTransferRule,
    ...item,
    from_account_id: item?.from_account_id ?? "",
    to_account_id: item?.to_account_id ?? "",
    pairing_window_minutes: String(item?.pairing_window_minutes || 5),
    notes: item?.notes || "",
    create_reverse: false,
});

const accountOptionLabel = (account) => {
    const title = account?.label || account?.account_title || account?.bank_name || "Счет";
    return `${title} / ${account?.account_number || "без IBAN"}`;
};

const Field = ({ label, children, className = "" }) => (
    <div className={`og-fld${className ? ` ${className}` : ""}`}>
        <label>{label}</label>
        {children}
    </div>
);

const Check = ({ checked, onChange, disabled, children }) => (
    <label className={`og-check${checked ? " ck" : ""}${disabled ? " disabled" : ""}`}>
        <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} style={hiddenCheckboxStyle} />
        <span className="box">{checked ? "✓" : ""}</span>
        {children}
    </label>
);

const BusinessOrganizationsPage = ({ embedded = false }) => {
    const [organizations, setOrganizations] = useState([]);
    const [stores, setStores] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [activeTab, setActiveTab] = useState("organization");
    const [organizationDraft, setOrganizationDraft] = useState(emptyOrganization);
    const [accountDraft, setAccountDraft] = useState(emptyAccount);
    const [editingAccountId, setEditingAccountId] = useState(null);
    const [registerDraft, setRegisterDraft] = useState(emptyRegister);
    const [editingRegisterId, setEditingRegisterId] = useState(null);
    const [exclusionDraft, setExclusionDraft] = useState(emptyExclusion);
    const [editingExclusionId, setEditingExclusionId] = useState(null);
    const [internalTransferRules, setInternalTransferRules] = useState([]);
    const [internalTransferRuleDraft, setInternalTransferRuleDraft] = useState(emptyInternalTransferRule);
    const [editingInternalTransferRuleId, setEditingInternalTransferRuleId] = useState(null);
    const [testRegisterId, setTestRegisterId] = useState("");
    const [testResult, setTestResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const selectedOrganization = useMemo(
        () => organizations.find((item) => item.id === selectedId) || null,
        [organizations, selectedId],
    );

    const loadInternalTransferRules = useCallback(async (organizationId) => {
        if (!organizationId) {
            setInternalTransferRules([]);
            return;
        }
        const rows = await getInternalTransferRules(organizationId);
        setInternalTransferRules(rows);
    }, []);

    const loadData = useCallback(async (preferredSelectedId) => {
        setLoading(true);
        setError("");
        try {
            const [organizationRows, storeRows] = await Promise.all([
                getBusinessOrganizations(),
                getBusinessStores(),
            ]);
            setOrganizations(organizationRows);
            setStores(storeRows);
            const hasPreferredSelection = preferredSelectedId !== undefined;
            const nextSelected = hasPreferredSelection ? preferredSelectedId : organizationRows[0]?.id || null;
            setSelectedId(nextSelected);
            const nextOrganization = organizationRows.find((item) => item.id === nextSelected) || (hasPreferredSelection ? null : organizationRows[0] || null);
            setOrganizationDraft(nextOrganization ? organizationToDraft(nextOrganization) : emptyOrganization);
            if (nextSelected) {
                await loadInternalTransferRules(nextSelected);
            } else {
                setInternalTransferRules([]);
            }
        } catch (loadError) {
            handleAuthError(loadError);
            setError(formatError(loadError, "Не удалось загрузить юрлица."));
        } finally {
            setLoading(false);
        }
    }, [loadInternalTransferRules]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const selectOrganization = (item) => {
        setSelectedId(item?.id || null);
        setOrganizationDraft(item ? organizationToDraft(item) : emptyOrganization);
        setAccountDraft(emptyAccount);
        setEditingAccountId(null);
        setRegisterDraft(emptyRegister);
        setEditingRegisterId(null);
        setExclusionDraft(emptyExclusion);
        setEditingExclusionId(null);
        setInternalTransferRuleDraft(emptyInternalTransferRule);
        setEditingInternalTransferRuleId(null);
        setSuccess("");
        setError("");
        if (item?.id) {
            loadInternalTransferRules(item.id).catch((loadError) => {
                handleAuthError(loadError);
                setError(formatError(loadError, "Не удалось загрузить правила внутренних переводов."));
            });
        } else {
            setInternalTransferRules([]);
        }
    };

    const saveOrganization = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const payload = {
                ...organizationDraft,
                salesdrive_organization_id: normalizeText(organizationDraft.salesdrive_organization_id),
                short_name: normalizeText(organizationDraft.short_name),
                full_name: normalizeText(organizationDraft.full_name),
                tax_id: normalizeText(organizationDraft.tax_id),
                signer_name: normalizeText(organizationDraft.signer_name),
                signer_position: normalizeText(organizationDraft.signer_position),
                chief_accountant_name: normalizeText(organizationDraft.chief_accountant_name),
                cashier_name: normalizeText(organizationDraft.cashier_name),
                address: normalizeText(organizationDraft.address),
                postal_code: normalizeText(organizationDraft.postal_code),
                city: normalizeText(organizationDraft.city),
                region: normalizeText(organizationDraft.region),
                country: normalizeText(organizationDraft.country),
                phone: normalizeText(organizationDraft.phone),
                notes: normalizeText(organizationDraft.notes),
            };
            const saved = selectedOrganization
                ? await updateBusinessOrganization(selectedOrganization.id, payload)
                : await createBusinessOrganization(payload);
            await loadData(saved.id);
            setSelectedId(saved.id);
            setOrganizationDraft(organizationToDraft(saved));
            setSuccess("Юрлицо сохранено.");
        } catch (saveError) {
            handleAuthError(saveError);
            setError(formatError(saveError, "Не удалось сохранить юрлицо."));
        } finally {
            setSaving(false);
        }
    };

    const saveAccount = async () => {
        if (!selectedOrganization) return;
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...accountDraft,
                salesdrive_account_id: normalizeText(accountDraft.salesdrive_account_id),
                account_number: normalizeText(accountDraft.account_number),
                account_title: normalizeText(accountDraft.account_title),
                label: normalizeText(accountDraft.label),
                card_mask: normalizeText(accountDraft.card_mask),
                currency: normalizeText(accountDraft.currency) || "UAH",
                bank_name: normalizeText(accountDraft.bank_name),
                mfo: normalizeText(accountDraft.mfo),
            };
            if (editingAccountId) {
                await updateBusinessAccount(selectedOrganization.id, editingAccountId, payload);
            } else {
                await createBusinessAccount(selectedOrganization.id, payload);
            }
            await loadData(selectedOrganization.id);
            setAccountDraft(emptyAccount);
            setEditingAccountId(null);
            setSuccess("Счет сохранен.");
        } catch (saveError) {
            handleAuthError(saveError);
            setError(formatError(saveError, "Не удалось сохранить счет."));
        } finally {
            setSaving(false);
        }
    };

    const saveRegister = async () => {
        if (!selectedOrganization) return;
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...registerDraft,
                business_store_id: normalizeId(registerDraft.business_store_id),
                enterprise_code: normalizeText(registerDraft.enterprise_code),
                register_name: normalizeText(registerDraft.register_name),
                cash_register_code: normalizeText(registerDraft.cash_register_code),
                checkbox_license_key: normalizeText(registerDraft.checkbox_license_key),
                cashier_login: normalizeText(registerDraft.cashier_login),
                cashier_password: normalizeText(registerDraft.cashier_password),
                cashier_pin: normalizeText(registerDraft.cashier_pin),
                api_base_url: normalizeText(registerDraft.api_base_url),
                shift_open_time: normalizeText(registerDraft.shift_open_time),
                shift_close_time: normalizeText(registerDraft.shift_close_time),
                timezone: normalizeText(registerDraft.timezone) || "Europe/Kiev",
                notes: normalizeText(registerDraft.notes),
            };
            if (editingRegisterId) {
                await updateCheckboxRegister(selectedOrganization.id, editingRegisterId, payload);
            } else {
                await createCheckboxRegister(selectedOrganization.id, payload);
            }
            await loadData(selectedOrganization.id);
            setRegisterDraft(emptyRegister);
            setEditingRegisterId(null);
            setSuccess("Касса сохранена.");
        } catch (saveError) {
            handleAuthError(saveError);
            setError(formatError(saveError, "Не удалось сохранить кассу."));
        } finally {
            setSaving(false);
        }
    };

    const saveExclusion = async () => {
        if (!selectedOrganization) return;
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...exclusionDraft,
                cash_register_id: normalizeId(exclusionDraft.cash_register_id),
                supplier_code: normalizeText(exclusionDraft.supplier_code),
                supplier_name: normalizeText(exclusionDraft.supplier_name),
                comment: normalizeText(exclusionDraft.comment),
            };
            if (editingExclusionId) {
                await updateCheckboxExclusion(selectedOrganization.id, editingExclusionId, payload);
            } else {
                await createCheckboxExclusion(selectedOrganization.id, payload);
            }
            await loadData(selectedOrganization.id);
            setExclusionDraft(emptyExclusion);
            setEditingExclusionId(null);
            setSuccess("Исключение сохранено.");
        } catch (saveError) {
            handleAuthError(saveError);
            setError(formatError(saveError, "Не удалось сохранить исключение."));
        } finally {
            setSaving(false);
        }
    };

    const saveInternalTransferRule = async () => {
        if (!selectedOrganization) return;
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const payload = {
                from_account_id: Number(internalTransferRuleDraft.from_account_id),
                to_account_id: Number(internalTransferRuleDraft.to_account_id),
                is_active: Boolean(internalTransferRuleDraft.is_active),
                pairing_window_minutes: Number(internalTransferRuleDraft.pairing_window_minutes || 5),
                require_exact_amount: Boolean(internalTransferRuleDraft.require_exact_amount),
                notes: normalizeText(internalTransferRuleDraft.notes),
                create_reverse: !editingInternalTransferRuleId && Boolean(internalTransferRuleDraft.create_reverse),
            };
            if (!payload.from_account_id || !payload.to_account_id) {
                throw new Error("Выберите оба счета для правила.");
            }
            if (editingInternalTransferRuleId) {
                await updateInternalTransferRule(selectedOrganization.id, editingInternalTransferRuleId, payload);
            } else {
                await createInternalTransferRule(selectedOrganization.id, payload);
            }
            await loadInternalTransferRules(selectedOrganization.id);
            setInternalTransferRuleDraft(emptyInternalTransferRule);
            setEditingInternalTransferRuleId(null);
            setSuccess("Правило сохранено. Для применения к уже импортированным платежам пересчитайте период.");
        } catch (saveError) {
            handleAuthError(saveError);
            setError(formatError(saveError, "Не удалось сохранить правило внутренних переводов."));
        } finally {
            setSaving(false);
        }
    };

    const recalculateCurrentPaymentPeriod = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const period = currentMonthPeriod();
            const result = await recalculatePaymentReport(period);
            setSuccess(`Пересчет выполнен: внутренних платежей ${result.internal_payments || 0}, пар ${result.internal_pairs || 0}.`);
        } catch (recalculateError) {
            handleAuthError(recalculateError);
            setError(formatError(recalculateError, "Не удалось пересчитать текущий период."));
        } finally {
            setSaving(false);
        }
    };

    const runRegisterTest = async (action) => {
        if (!selectedOrganization || !testRegisterId) return;
        setSaving(true);
        setError("");
        setTestResult(null);
        try {
            const result = await testCheckboxRegister(selectedOrganization.id, testRegisterId, action);
            setTestResult(result);
            setSuccess(`Checkbox test ${action}: ok`);
        } catch (testError) {
            handleAuthError(testError);
            setError(formatError(testError, "Не удалось выполнить тест кассы."));
        } finally {
            setSaving(false);
        }
    };

    const tabButton = (key, label) => (
        <button
            type="button"
            className={`og-tab${activeTab === key ? " active" : ""}`}
            onClick={() => setActiveTab(key)}
        >
            {label}
        </button>
    );

    return (
        <div style={embedded ? embeddedPageStyle : pageStyle}>
            {!embedded && (
                <div className="og-card" style={{ padding: "20px 24px" }}>
                    <h1 style={{ margin: 0, fontSize: "28px", color: "var(--og-text)" }}>Юрлица и кассы</h1>
                    <p style={{ margin: "6px 0 0", color: "var(--og-muted)", fontSize: 14, lineHeight: 1.5 }}>Юрлица, банковские счета, Checkbox-кассы и исключения для фискализации.</p>
                </div>
            )}
            {error ? (
                <div className="og-callout danger"><span className="ic">⚠</span><div>{error}</div></div>
            ) : null}
            {success ? (
                <div className="og-callout good"><span className="ic">✓</span><div>{success}</div></div>
            ) : null}
            {loading ? (
                <div className="og-card" style={{ padding: "20px 24px" }}>Загрузка...</div>
            ) : (
                <div className="og-split">
                    <aside className="og-card og-picker">
                        <h2>Юрлица</h2>
                        <div className="og-picker-actions">
                            <button type="button" className="og-btn og-btn-primary og-btn-sm" onClick={() => selectOrganization(null)}>
                                + Добавить юрлицо
                            </button>
                        </div>
                        <div className="og-list">
                            {organizations.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`og-list-item${item.id === selectedId ? " sel" : ""}`}
                                    onClick={() => selectOrganization(item)}
                                >
                                    <span className="nm">{item.short_name}</span>
                                    <span className="meta">
                                        SalesDrive {item.salesdrive_organization_id || "—"} / {item.tax_id || "без ЕДРПОУ"}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </aside>

                    <section className="og-detail">
                        <div className="og-card" style={{ padding: "16px 18px 20px" }}>
                            <div className="og-tabbar">
                                {tabButton("organization", "Основные данные юрлица")}
                                {tabButton("accounts", "Банковские счета")}
                                {tabButton("internal-transfers", "Внутренние переводы")}
                                {tabButton("registers", "Кассы Checkbox")}
                                {tabButton("exclusions", "Исключения чеков")}
                                {tabButton("tests", "Тесты")}
                            </div>

                            {activeTab === "organization" ? (
                                <div style={{ display: "grid", gap: "16px" }}>
                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">ⓘ</span><h4>Реквизиты юрлица</h4></div>
                                        <div className="og-grid2">
                                            <Field label="Короткое наименование">
                                                <input className="og-inp" value={organizationDraft.short_name} onChange={(e) => setOrganizationDraft((p) => ({ ...p, short_name: e.target.value }))} />
                                            </Field>
                                            <Field label="SalesDrive ID юрлица">
                                                <input className="og-inp" value={organizationDraft.salesdrive_organization_id} onChange={(e) => setOrganizationDraft((p) => ({ ...p, salesdrive_organization_id: e.target.value }))} />
                                            </Field>
                                            <Field label="Полное наименование" className="og-col-span">
                                                <input className="og-inp" value={organizationDraft.full_name} onChange={(e) => setOrganizationDraft((p) => ({ ...p, full_name: e.target.value }))} />
                                            </Field>
                                            <Field label="ЕДРПОУ / РНОКПП">
                                                <input className="og-inp" value={organizationDraft.tax_id} onChange={(e) => setOrganizationDraft((p) => ({ ...p, tax_id: e.target.value }))} />
                                            </Field>
                                            <Field label="Тип">
                                                <select className="og-inp" value={organizationDraft.entity_type} onChange={(e) => setOrganizationDraft((p) => ({ ...p, entity_type: e.target.value }))}>
                                                    <option value="fop">ФОП</option>
                                                    <option value="company">Компания</option>
                                                    <option value="individual">Физлицо</option>
                                                    <option value="other">Другое</option>
                                                </select>
                                            </Field>
                                            <Field label="Телефон">
                                                <input className="og-inp" value={organizationDraft.phone} onChange={(e) => setOrganizationDraft((p) => ({ ...p, phone: e.target.value }))} />
                                            </Field>
                                            <Field label="Адрес" className="og-col-span">
                                                <input className="og-inp" value={organizationDraft.address} onChange={(e) => setOrganizationDraft((p) => ({ ...p, address: e.target.value }))} />
                                            </Field>
                                            <Field label="Город">
                                                <input className="og-inp" value={organizationDraft.city} onChange={(e) => setOrganizationDraft((p) => ({ ...p, city: e.target.value }))} />
                                            </Field>
                                            <Field label="Подписант">
                                                <input className="og-inp" value={organizationDraft.signer_name} onChange={(e) => setOrganizationDraft((p) => ({ ...p, signer_name: e.target.value }))} />
                                            </Field>
                                            <Field label="Должность подписанта">
                                                <input className="og-inp" value={organizationDraft.signer_position} onChange={(e) => setOrganizationDraft((p) => ({ ...p, signer_position: e.target.value }))} />
                                            </Field>
                                            <Field label="Главный бухгалтер">
                                                <input className="og-inp" value={organizationDraft.chief_accountant_name} onChange={(e) => setOrganizationDraft((p) => ({ ...p, chief_accountant_name: e.target.value }))} />
                                            </Field>
                                            <Field label="Кассир / ответственное лицо">
                                                <input className="og-inp" value={organizationDraft.cashier_name} onChange={(e) => setOrganizationDraft((p) => ({ ...p, cashier_name: e.target.value }))} />
                                            </Field>
                                            <Check checked={organizationDraft.vat_payer} onChange={(e) => setOrganizationDraft((p) => ({ ...p, vat_payer: e.target.checked, vat_enabled: e.target.checked }))}>
                                                Плательщик НДС
                                            </Check>
                                            <Check checked={organizationDraft.is_active} onChange={(e) => setOrganizationDraft((p) => ({ ...p, is_active: e.target.checked }))}>
                                                Юрлицо активно
                                            </Check>
                                            <Field label="Комментарий" className="og-col-span">
                                                <textarea className="og-inp" value={organizationDraft.notes} onChange={(e) => setOrganizationDraft((p) => ({ ...p, notes: e.target.value }))} />
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">↔</span><h4>Подбор поставщика для заказов</h4></div>
                                        <div className="og-grid2">
                                            <Field label="Режим подбора" className="og-col-span">
                                                <select
                                                    className="og-inp"
                                                    value={organizationDraft.order_supplier_selection_policy}
                                                    onChange={(e) => setOrganizationDraft((p) => ({ ...p, order_supplier_selection_policy: e.target.value }))}
                                                >
                                                    <option value="lowest_cost">Минимальная закупочная цена — текущий режим</option>
                                                    <option value="preserve_price_source">Сохранять источник цены позиции</option>
                                                </select>
                                            </Field>
                                        </div>
                                        {organizationDraft.order_supplier_selection_policy === "preserve_price_source" ? (
                                            <div className="og-mini" style={{ marginTop: "10px" }}>
                                                Выбирается поставщик с совпадающей ценой позиции ±0,50 грн и достаточным остатком. При отсутствии совпадения используется обычный подбор.
                                            </div>
                                        ) : null}
                                        <div className="og-mini" style={{ marginTop: "6px" }}>
                                            Применяется ко всем магазинам этого юрлица.
                                        </div>
                                    </div>
                                    <div className="og-save-bar">
                                        <button type="button" className="og-btn og-btn-primary" onClick={saveOrganization} disabled={saving}>Сохранить юрлицо</button>
                                    </div>
                                </div>
                            ) : null}

                            {activeTab === "accounts" && selectedOrganization ? (
                                <div style={{ display: "grid", gap: "16px" }}>
                                    <div className="og-tbl-wrap">
                                        <table className="og-tbl">
                                            <thead><tr><th>Счет</th><th>Банк</th><th>МФО</th><th>Статус</th></tr></thead>
                                            <tbody>{(selectedOrganization.accounts || []).map((item) => (
                                                <tr key={item.id} className="click" onClick={() => { setEditingAccountId(item.id); setAccountDraft(accountToDraft(item)); }}>
                                                    <td><b>{item.account_number}</b><div className="og-mini">{item.label || item.account_title || ""}</div></td>
                                                    <td>{item.bank_name || "—"}</td>
                                                    <td>{item.mfo || "—"}</td>
                                                    <td>{item.is_active ? <span className="og-pill g">активен</span> : <span className="og-pill off">выключен</span>}</td>
                                                </tr>
                                            ))}</tbody>
                                        </table>
                                    </div>
                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">＋</span><h4>Банковский счёт</h4></div>
                                        <div className="og-grid2">
                                            <Field label="IBAN / счет" className="og-col-span"><input className="og-inp" value={accountDraft.account_number} onChange={(e) => setAccountDraft((p) => ({ ...p, account_number: e.target.value }))} /></Field>
                                            <Field label="Метка"><input className="og-inp" value={accountDraft.label} onChange={(e) => setAccountDraft((p) => ({ ...p, label: e.target.value }))} /></Field>
                                            <Field label="Банк"><input className="og-inp" value={accountDraft.bank_name} onChange={(e) => setAccountDraft((p) => ({ ...p, bank_name: e.target.value }))} /></Field>
                                            <Field label="МФО"><input className="og-inp" value={accountDraft.mfo} onChange={(e) => setAccountDraft((p) => ({ ...p, mfo: e.target.value }))} /></Field>
                                            <Check checked={accountDraft.is_active} onChange={(e) => setAccountDraft((p) => ({ ...p, is_active: e.target.checked }))}>
                                                Счет активен (неактивные не попадают в отчеты)
                                            </Check>
                                        </div>
                                        <div className="og-save-bar notop">
                                            <button type="button" className="og-btn og-btn-primary og-btn-sm" onClick={saveAccount} disabled={saving}>{editingAccountId ? "Сохранить счет" : "Добавить счет"}</button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeTab === "internal-transfers" && selectedOrganization ? (
                                <div style={{ display: "grid", gap: "16px" }}>
                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                                        <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={recalculateCurrentPaymentPeriod} disabled={saving}>Пересчитать текущий период</button>
                                    </div>
                                    <div className="og-tbl-wrap">
                                        <table className="og-tbl">
                                            <thead>
                                                <tr>
                                                    <th>Со счета</th>
                                                    <th>На счет</th>
                                                    <th>Окно</th>
                                                    <th>Сумма</th>
                                                    <th>Статус</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {internalTransferRules.length ? internalTransferRules.map((item) => (
                                                    <tr
                                                        key={item.id}
                                                        className="click"
                                                        onClick={() => {
                                                            setEditingInternalTransferRuleId(item.id);
                                                            setInternalTransferRuleDraft(internalTransferRuleToDraft(item));
                                                        }}
                                                    >
                                                        <td>{item.from_account_label || "Счет"}<div className="og-mini">{item.from_account_number || ""}</div></td>
                                                        <td>{item.to_account_label || "Счет"}<div className="og-mini">{item.to_account_number || ""}</div></td>
                                                        <td>{item.pairing_window_minutes} мин</td>
                                                        <td>{item.require_exact_amount ? "точное совпадение" : "может отличаться"}</td>
                                                        <td>{item.is_active ? <span className="og-pill g">активен</span> : <span className="og-pill off">выключен</span>}</td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan={5}>Правила для этого юрлица не настроены.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">⇄</span><h4>Правило перевода</h4></div>
                                        <div className="og-grid3">
                                            <Field label="Со счета">
                                                <select
                                                    className="og-inp"
                                                    value={internalTransferRuleDraft.from_account_id}
                                                    onChange={(e) => setInternalTransferRuleDraft((p) => ({ ...p, from_account_id: e.target.value }))}
                                                >
                                                    <option value="">Выберите счет</option>
                                                    {(selectedOrganization.accounts || []).map((item) => (
                                                        <option key={item.id} value={item.id}>{accountOptionLabel(item)}</option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="На счет">
                                                <select
                                                    className="og-inp"
                                                    value={internalTransferRuleDraft.to_account_id}
                                                    onChange={(e) => setInternalTransferRuleDraft((p) => ({ ...p, to_account_id: e.target.value }))}
                                                >
                                                    <option value="">Выберите счет</option>
                                                    {(selectedOrganization.accounts || []).map((item) => (
                                                        <option key={item.id} value={item.id}>{accountOptionLabel(item)}</option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Окно матчинга, минут">
                                                <input
                                                    className="og-inp"
                                                    type="number"
                                                    min="1"
                                                    max="1440"
                                                    value={internalTransferRuleDraft.pairing_window_minutes}
                                                    onChange={(e) => setInternalTransferRuleDraft((p) => ({ ...p, pairing_window_minutes: e.target.value }))}
                                                />
                                            </Field>
                                            <Field label="Комментарий" className="og-col-span">
                                                <input
                                                    className="og-inp"
                                                    value={internalTransferRuleDraft.notes}
                                                    onChange={(e) => setInternalTransferRuleDraft((p) => ({ ...p, notes: e.target.value }))}
                                                />
                                            </Field>
                                            <Check checked={internalTransferRuleDraft.require_exact_amount} onChange={(e) => setInternalTransferRuleDraft((p) => ({ ...p, require_exact_amount: e.target.checked }))}>
                                                Требовать точное совпадение суммы
                                            </Check>
                                            <Check checked={internalTransferRuleDraft.is_active} onChange={(e) => setInternalTransferRuleDraft((p) => ({ ...p, is_active: e.target.checked }))}>
                                                Правило активно
                                            </Check>
                                            {!editingInternalTransferRuleId ? (
                                                <Check checked={internalTransferRuleDraft.create_reverse} onChange={(e) => setInternalTransferRuleDraft((p) => ({ ...p, create_reverse: e.target.checked }))}>
                                                    Создать обратное направление
                                                </Check>
                                            ) : null}
                                        </div>
                                        <div className="og-save-bar notop">
                                            <button type="button" className="og-btn og-btn-primary og-btn-sm" onClick={saveInternalTransferRule} disabled={saving || (selectedOrganization.accounts || []).length < 2}>
                                                {editingInternalTransferRuleId ? "Сохранить правило" : "Добавить правило"}
                                            </button>
                                            {editingInternalTransferRuleId ? (
                                                <button
                                                    type="button"
                                                    className="og-btn og-btn-ghost og-btn-sm"
                                                    onClick={() => {
                                                        setEditingInternalTransferRuleId(null);
                                                        setInternalTransferRuleDraft(emptyInternalTransferRule);
                                                    }}
                                                    disabled={saving}
                                                >
                                                    Новое правило
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeTab === "registers" && selectedOrganization ? (
                                <div style={{ display: "grid", gap: "16px" }}>
                                    <div className="og-tbl-wrap">
                                        <table className="og-tbl">
                                            <thead><tr><th>Касса</th><th>Привязка</th><th>Смена</th><th>Секреты</th></tr></thead>
                                            <tbody>{(selectedOrganization.cash_registers || []).map((item) => (
                                                <tr key={item.id} className="click" onClick={() => { setEditingRegisterId(item.id); setRegisterDraft(registerToDraft(item)); }}>
                                                    <td><b>{item.register_name}</b><div className="og-mini">{item.cash_register_code} · {item.is_active ? "активна" : "отключена"}</div></td>
                                                    <td>{item.business_store_id ? `store ${item.business_store_id}` : "всё юрлицо"}<div className="og-mini">{item.enterprise_code || ""}</div></td>
                                                    <td>{item.shift_open_mode}<div className="og-mini">закрытие {item.shift_close_time || "—"}</div></td>
                                                    <td>license {item.checkbox_license_key_set ? "есть" : "нет"}<div className="og-mini">pin {item.cashier_pin_set ? "есть" : "нет"}</div></td>
                                                </tr>
                                            ))}</tbody>
                                        </table>
                                    </div>

                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">🆔</span><h4>Идентификация</h4></div>
                                        <div className="og-grid2">
                                            <Field label="Название кассы"><input className="og-inp" value={registerDraft.register_name} onChange={(e) => setRegisterDraft((p) => ({ ...p, register_name: e.target.value }))} /></Field>
                                            <Field label="Код кассы / alias"><input className="og-inp" value={registerDraft.cash_register_code} onChange={(e) => setRegisterDraft((p) => ({ ...p, cash_register_code: e.target.value }))} /></Field>
                                            <Field label="Магазин">
                                                <select className="og-inp" value={registerDraft.business_store_id} onChange={(e) => setRegisterDraft((p) => ({ ...p, business_store_id: e.target.value }))}>
                                                    <option value="">Всё юрлицо</option>
                                                    {stores.map((store) => <option key={store.id} value={store.id}>{store.store_name} / {store.tabletki_branch}</option>)}
                                                </select>
                                            </Field>
                                            <Field label="Enterprise code"><input className="og-inp" value={registerDraft.enterprise_code} onChange={(e) => setRegisterDraft((p) => ({ ...p, enterprise_code: e.target.value }))} /></Field>
                                        </div>
                                    </div>

                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">🔑</span><h4>Доступ и секреты</h4><span className="hint">не показываются повторно</span></div>
                                        <div className="og-grid3">
                                            <Field label="License key"><input className="og-inp" type="password" placeholder={editingRegisterId ? "оставить пустым, чтобы не менять" : ""} value={registerDraft.checkbox_license_key} onChange={(e) => setRegisterDraft((p) => ({ ...p, checkbox_license_key: e.target.value }))} /></Field>
                                            <Field label="Cashier login"><input className="og-inp" type="password" value={registerDraft.cashier_login} onChange={(e) => setRegisterDraft((p) => ({ ...p, cashier_login: e.target.value }))} /></Field>
                                            <Field label="Cashier password"><input className="og-inp" type="password" placeholder={editingRegisterId ? "оставить пустым, чтобы не менять" : ""} value={registerDraft.cashier_password} onChange={(e) => setRegisterDraft((p) => ({ ...p, cashier_password: e.target.value }))} /></Field>
                                            <Field label="Cashier PIN"><input className="og-inp" type="password" placeholder={editingRegisterId ? "оставить пустым, чтобы не менять" : ""} value={registerDraft.cashier_pin} onChange={(e) => setRegisterDraft((p) => ({ ...p, cashier_pin: e.target.value }))} /></Field>
                                        </div>
                                    </div>

                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">⏱</span><h4>Режим смены</h4></div>
                                        <div className="og-grid2">
                                            <Field label="Открытие смены">
                                                <select className="og-inp" value={registerDraft.shift_open_mode} onChange={(e) => setRegisterDraft((p) => ({ ...p, shift_open_mode: e.target.value }))}>
                                                    <option value="manual">Вручную</option>
                                                    <option value="scheduled">По времени</option>
                                                    <option value="first_status_4">По первому статусу 4</option>
                                                    <option value="on_fiscalization">При фискализации</option>
                                                </select>
                                            </Field>
                                            <Field label="Закрытие смены"><input className="og-inp" value={registerDraft.shift_close_time} onChange={(e) => setRegisterDraft((p) => ({ ...p, shift_close_time: e.target.value }))} /></Field>
                                        </div>
                                    </div>

                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">🔔</span><h4>Уведомления и режим</h4></div>
                                        <div className="og-grid4">
                                            <Check checked={registerDraft.is_active} onChange={(e) => setRegisterDraft((p) => ({ ...p, is_active: e.target.checked }))}>Касса активна</Check>
                                            <Check checked={registerDraft.is_default} onChange={(e) => setRegisterDraft((p) => ({ ...p, is_default: e.target.checked }))}>Default</Check>
                                            <Check checked={registerDraft.is_test_mode} onChange={(e) => setRegisterDraft((p) => ({ ...p, is_test_mode: e.target.checked }))}>Test mode</Check>
                                            <Check checked={registerDraft.shift_notifications_enabled} onChange={(e) => setRegisterDraft((p) => ({ ...p, shift_notifications_enabled: e.target.checked }))}>Telegram смены</Check>
                                            <Check checked={registerDraft.receipt_notifications_enabled} onChange={(e) => setRegisterDraft((p) => ({ ...p, receipt_notifications_enabled: e.target.checked }))}>Telegram чеки</Check>
                                        </div>
                                    </div>

                                    <div className="og-save-bar">
                                        <button type="button" className="og-btn og-btn-primary" onClick={saveRegister} disabled={saving}>{editingRegisterId ? "Сохранить кассу" : "Добавить кассу"}</button>
                                    </div>
                                </div>
                            ) : null}

                            {activeTab === "exclusions" && selectedOrganization ? (
                                <div style={{ display: "grid", gap: "16px" }}>
                                    <div className="og-tbl-wrap">
                                        <table className="og-tbl">
                                            <thead><tr><th>Поставщик</th><th>Касса</th><th>Статус</th></tr></thead>
                                            <tbody>{(selectedOrganization.receipt_exclusions || []).map((item) => (
                                                <tr key={item.id} className="click" onClick={() => { setEditingExclusionId(item.id); setExclusionDraft(exclusionToDraft(item)); }}>
                                                    <td><b>{item.supplier_code}</b><div className="og-mini">{item.supplier_name || ""}</div></td>
                                                    <td>{item.cash_register_id ? `касса ${item.cash_register_id}` : "все кассы"}</td>
                                                    <td>{item.is_active ? <span className="og-pill g">активен</span> : <span className="og-pill off">выключен</span>}</td>
                                                </tr>
                                            ))}</tbody>
                                        </table>
                                    </div>
                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">＋</span><h4>Исключение чека</h4></div>
                                        <div className="og-grid3">
                                            <Field label="Код поставщика"><input className="og-inp" value={exclusionDraft.supplier_code} onChange={(e) => setExclusionDraft((p) => ({ ...p, supplier_code: e.target.value }))} /></Field>
                                            <Field label="Название"><input className="og-inp" value={exclusionDraft.supplier_name} onChange={(e) => setExclusionDraft((p) => ({ ...p, supplier_name: e.target.value }))} /></Field>
                                            <Field label="Касса">
                                                <select className="og-inp" value={exclusionDraft.cash_register_id} onChange={(e) => setExclusionDraft((p) => ({ ...p, cash_register_id: e.target.value }))}>
                                                    <option value="">Все кассы</option>
                                                    {(selectedOrganization.cash_registers || []).map((item) => <option key={item.id} value={item.id}>{item.register_name}</option>)}
                                                </select>
                                            </Field>
                                            <Check checked={exclusionDraft.is_active} onChange={(e) => setExclusionDraft((p) => ({ ...p, is_active: e.target.checked }))}>Активно</Check>
                                            <Field label="Комментарий" className="og-col-span"><input className="og-inp" value={exclusionDraft.comment} onChange={(e) => setExclusionDraft((p) => ({ ...p, comment: e.target.value }))} /></Field>
                                        </div>
                                        <div className="og-save-bar notop">
                                            <button type="button" className="og-btn og-btn-primary og-btn-sm" onClick={saveExclusion} disabled={saving}>{editingExclusionId ? "Сохранить исключение" : "Добавить исключение"}</button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeTab === "tests" && selectedOrganization ? (
                                <div style={{ display: "grid", gap: "16px" }}>
                                    <div className="og-block">
                                        <div className="og-bh"><span className="ic">🧪</span><h4>Проверка кассы</h4></div>
                                        <div className="og-grid2">
                                            <Field label="Касса">
                                                <select
                                                    className="og-inp"
                                                    value={testRegisterId}
                                                    onChange={(e) => setTestRegisterId(e.target.value)}
                                                >
                                                    <option value="">Выберите кассу</option>
                                                    {(selectedOrganization.cash_registers || []).map((item) => (
                                                        <option key={item.id} value={item.id}>
                                                            {item.register_name} / {item.cash_register_code}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Field>
                                        </div>
                                        <div className="og-save-bar notop">
                                            <button type="button" className="og-btn og-btn-primary og-btn-sm" onClick={() => runRegisterTest("auth")} disabled={saving || !testRegisterId}>Проверить авторизацию</button>
                                            <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => runRegisterTest("open_shift")} disabled={saving || !testRegisterId}>Открыть смену</button>
                                            <button type="button" className="og-btn og-btn-ghost og-btn-sm" onClick={() => runRegisterTest("close_shift")} disabled={saving || !testRegisterId}>Закрыть смену</button>
                                        </div>
                                    </div>
                                    {testResult ? (
                                        <div className="og-block" style={{ background: "var(--og-surface-2)" }}>
                                            <div className="og-bh"><span className="ic">▤</span><h4>Ответ</h4></div>
                                            <pre style={{ margin: 0, fontSize: 12, color: "var(--og-text-2)", whiteSpace: "pre-wrap" }}>
                                                {JSON.stringify(testResult, null, 2)}
                                            </pre>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default BusinessOrganizationsPage;
